import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Field } from '@base-ui/react/field';

// Fluid Functionalism input group, cut down to Base UI. The box matches the
// select trigger: always outlined, hover fills the inner surface. Fields in
// one group share that hover, so the field nearest the pointer is the one
// that fills. There is no sliding highlight; each field paints itself.

const InputGroupContext = createContext(null);

export function InputGroup({ children, size = 'default', className, ...props }) {
  const containerRef = useRef(null);
  const itemsRef = useRef(new Map());
  const rafRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(null);

  const registerItem = useCallback((index, element) => {
    if (element) itemsRef.current.set(index, element);
    else itemsRef.current.delete(index);
  }, []);

  const pick = (event) => {
    if (!(event.currentTarget instanceof Node) || !(event.target instanceof Node)) return;
    if (!event.currentTarget.contains(event.target)) return;
    const y = event.clientY;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      let closest = null;
      let closestDistance = Infinity;
      let containing = null;
      itemsRef.current.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        if (rect.height === 0) return;
        if (y >= rect.top && y <= rect.bottom) containing = index;
        const distance = Math.abs(y - (rect.top + rect.height / 2));
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = index;
        }
      });
      const next = containing ?? closest;
      setActiveIndex((current) => (current === next ? current : next));
    });
  };

  const leave = () => {
    cancelAnimationFrame(rafRef.current);
    setActiveIndex(null);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const contextValue = useMemo(
    () => ({ registerItem, activeIndex, size }),
    [registerItem, activeIndex, size],
  );

  return (
    <InputGroupContext.Provider value={contextValue}>
      <div
        {...props}
        ref={containerRef}
        className={['edl-input-group', className].filter(Boolean).join(' ')}
        data-size={size}
        onMouseEnter={pick}
        onMouseMove={pick}
        onMouseLeave={leave}
      >
        {children}
      </div>
    </InputGroupContext.Provider>
  );
}

export function InputField({
  label,
  labelHidden = false,
  index,
  value,
  onChange,
  placeholder,
  icon: Icon,
  error,
  disabled = false,
  className,
  id,
  ...props
}) {
  const group = useContext(InputGroupContext);
  const size = group?.size ?? 'default';
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const iconSize = size === 'compact' ? 14 : 16;
  const active = group?.activeIndex === index;

  useEffect(() => {
    const register = group?.registerItem;
    if (!register || index == null) return undefined;
    register(index, rootRef.current);
    return () => register(index, null);
  }, [group?.registerItem, index]);

  return (
    <Field.Root
      ref={rootRef}
      invalid={Boolean(error)}
      disabled={disabled}
      className={['edl-input-field', className].filter(Boolean).join(' ')}
      data-size={size}
      data-active={active ? '' : undefined}
    >
      <Field.Label className="edl-input-field__label" data-hidden={labelHidden ? '' : undefined}>
        {label}
      </Field.Label>
      <div
        className="edl-input-field__box"
        data-icon-start={Icon ? '' : undefined}
        onMouseDown={(event) => {
          if (event.target === inputRef.current) return;
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <span className="edl-input-field__surface" aria-hidden="true" />
        {Icon ? <Icon className="edl-input-field__icon" size={iconSize} strokeWidth={1.5} aria-hidden="true" /> : null}
        <Field.Control
          {...props}
          ref={inputRef}
          id={id}
          type="text"
          className="edl-input-field__control"
          value={value ?? ''}
          placeholder={placeholder}
          onValueChange={(next) => onChange?.(next)}
        />
      </div>
      {error ? (
        <Field.Error match className="edl-input-field__error">
          {error}
        </Field.Error>
      ) : null}
    </Field.Root>
  );
}
